import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  LinearProgress,
  Stack,
  Chip,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  ArrowUpward as ArrowUpIcon,
} from "@mui/icons-material";

const DashboardMetrics = () => {
  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 0 }}>
        <Typography variant="h5" component="h1" fontWeight="bold">
          Dashboard
        </Typography>
      </Box>

      {/* Welcome message */}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Welcome back, Super Admin. Here's the platform overview.
      </Typography>

      {/* Filter and Export */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3, gap: 2 }}>
        <FormControl
          sx={{ minWidth: 150, bgcolor: "white" }}
          variant="outlined"
        >
          <Select
            value="last30days"
            displayEmpty
            startAdornment={
              <CalendarIcon sx={{ mr: 1, color: "text.secondary" }} />
            }
            sx={{
              borderRadius: 2,
              height: "40px",
              fontSize: "0.9rem",
            }}
          >
            <MenuItem value="last30days" sx={{ fontSize: "0.85rem" }}>
              Last 30 Days
            </MenuItem>
            <MenuItem value="last7days" sx={{ fontSize: "0.85rem" }}>
              Last 7 Days
            </MenuItem>
            <MenuItem value="thisMonth" sx={{ fontSize: "0.85rem" }}>
              This Month
            </MenuItem>
            <MenuItem value="lastMonth" sx={{ fontSize: "0.85rem" }}>
              Last Month
            </MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          sx={{
            bgcolor: "#2563eb",
            borderRadius: 2,
            px: 3,
            height: "40px",
            textTransform: "capitalize",
          }}
        >
          Export Data
        </Button>
      </Box>

      {/* Metric Cards */}
      <Grid container spacing={3}>
        {/* Total Companies */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              height: "100%",
              border: "1px solid #e5e7eb",
              maxHeight: 150,
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Total Companies
              </Typography>
              <Chip
                label="+12% ↑"
                size="small"
                sx={{
                  bgcolor: "rgba(37, 99, 235, 0.1)",
                  color: "#2563eb",
                  fontWeight: "medium",
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="h5"
                component="div"
                fontWeight="bold"
                sx={{ mb: 1 }}
              >
                24
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                active accounts
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={75}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "rgba(37, 99, 235, 0.1)",
                "& .MuiLinearProgress-bar": {
                  bgcolor: "#2563eb",
                },
              }}
            />
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                0
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Target: 32
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Total Users */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              height: "100%",
              border: "1px solid #e5e7eb",
              maxHeight: 150,
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
              <Chip
                label="+24% ↑"
                size="small"
                sx={{
                  bgcolor: "rgba(34, 197, 94, 0.1)",
                  color: "#22c55e",
                  fontWeight: "medium",
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="h5"
                component="div"
                fontWeight="bold"
                sx={{ mb: 1 }}
              >
                1,247
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                active users
              </Typography>
            </Box>

            <Stack spacing={0}>
              <Grid container>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "#a855f7",
                      }}
                    />
                    <Typography variant="body2">Admin: 946</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "#6366f1",
                      }}
                    />
                    <Typography variant="body2">QA: 215</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "#a855f7",
                      }}
                    />
                    <Typography variant="body2">Agents: 946</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Stack>
          </Paper>
        </Grid>

        {/* Minutes Analyzed */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              height: "100%",
              border: "1px solid #e5e7eb",
              maxHeight: 150,
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Minutes Analyzed
              </Typography>
              <Chip
                label="+8% ↑"
                size="small"
                sx={{
                  bgcolor: "rgba(96, 165, 250, 0.1)",
                  color: "#60a5fa",
                  fontWeight: "medium",
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="h5"
                component="div"
                fontWeight="bold"
                sx={{ mb: 1 }}
              >
                138,492
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                this month
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: "#2563eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ArrowUpIcon sx={{ color: "white", fontSize: 16 }} />
              </Box>
              <Typography variant="body2" fontWeight="medium">
                +15.8% from last period
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Revenue */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              height: "100%",
              border: "1px solid #e5e7eb",
              maxHeight: 150,
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Revenue
              </Typography>
              <Chip
                label="+16% ↑"
                size="small"
                sx={{
                  bgcolor: "rgba(34, 197, 94, 0.1)",
                  color: "#22c55e",
                  fontWeight: "medium",
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 1,
                mb: 1,
              }}
            >
              <Typography variant="h5" component="div" fontWeight="bold">
                $83,492
              </Typography>
              <Typography variant="body2" color="text.secondary">
                this month
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Monthly Goal: $100,000
            </Typography>
            <LinearProgress
              variant="determinate"
              value={83}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "rgba(34, 197, 94, 0.1)",
                "& .MuiLinearProgress-bar": {
                  bgcolor: "#22c55e",
                },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Typography variant="body2" fontWeight="medium">
                83%
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardMetrics;
